import { useDefaultParameterVisit } from "./useDefaultParameterVisit";
import { ReviewFixedMixesButton } from "./ReviewFixedMixesButton";
import { MaterialDefaultsEditorLayout } from "./MaterialDefaultsEditorLayout";
import { RevertDefaultsButton } from "./RevertDefaultsButton";
import { useCallback, useEffect, useMemo } from "react";
import { useAppDispatch } from "store/store";
import { backendApi } from "store/api/enhancedApi";
import { useNotifyStatus } from "contexts/status";
import { isFetchBaseQueryError } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import {
  MaterialRead,
  MaterialPhysicsSetRead,
  MaterialPhysicsOverride,
  useGetMaterialPhysicsSetQuery,
  useUpdateDefaultMaterialPhysicsMutation,
} from "store/api/generatedApi";
import { useDefaultParametersContext } from "./context";
import { useDefaultSetDraft } from "./useDefaultSetDraft";
import {
  physicsHasChanged,
  replacePhysicsOverride,
  editablePhysics,
  editablePhysicsEqual,
  physicsMatchesDefaults,
} from "./materialPhysicsEdits";
import { MaterialPhysicsDefaultsTable } from "./MaterialPhysicsDefaultsTable";
import { useMaterialPropertiesFeasibility } from "./useMaterialPropertiesFeasibility";

const EMPTY_OVERRIDES: MaterialPhysicsOverride[] = [];

export const MaterialPhysicsDefaultsEditor = ({
  isActive,
  setConfirmOpen,
  defaultSet,
  materials,
  refresh,
}: {
  isActive: boolean;
  setConfirmOpen: (open: boolean) => void;
  defaultSet: MaterialPhysicsSetRead;
  materials: MaterialRead[];
  refresh: () => Promise<unknown>;
}) => {
  const tenantName = useTenant();
  useDefaultParameterVisit({
    tenantName,
    page: "physics",
    isActive,
  });
  const scrapchef = useGetMaterialPhysicsSetQuery({
    tenantName,
    materialPhysicsSetId: "default",
    source: "scrapchef",
  });
  const dispatch = useAppDispatch();
  const { t } = useTenantTranslation();
  const notify = useNotifyStatus();
  const { setTabActions } = useDefaultParametersContext();
  const { source, overrides, setOverrides, discard, isStale, save } =
    useDefaultSetDraft({
      defaultSet,
      emptyOverrides: EMPTY_OVERRIDES,
      hasChanged: physicsHasChanged,
    });
  const [updateDefaults, { isLoading: isSaving }] =
    useUpdateDefaultMaterialPhysicsMutation();
  const candidate = useMemo(
    () => ({
      material_physics_overrides: overrides,
      material_chemistry_overrides: null,
    }),
    [overrides]
  );
  const {
    request,
    conflicts,
    isChecking,
    hasError,
    chefGroupNameById,
    limitLabelBySignature,
  } = useMaterialPropertiesFeasibility({ isStale, candidate });
  const hasChanges = overrides.length > 0;
  const onSave = useCallback(
    async (afterSave?: () => void) => {
      if (!hasChanges || isSaving || request === null) return;
      try {
        await save(async (source, overrides) => {
          const savedSet = await updateDefaults({
            tenantName,
            updateDefaultMaterialPhysicsRequest: {
              source_material_physics_set_id: source.id,
              material_physics_overrides: overrides,
            },
          }).unwrap();
          await dispatch(
            backendApi.util.upsertQueryData(
              "getMaterialPhysicsSet",
              { tenantName, materialPhysicsSetId: "default" },
              savedSet
            )
          );
        });
        notify({ text: t("defaultsSaved"), type: "success" });
        afterSave?.();
      } catch (error) {
        const stale = isFetchBaseQueryError(error) && error.status === 409;
        notify({
          text: t(stale ? "defaultsOutOfDate" : "errorSavingChanges"),
          type: "error",
        });
        if (stale) await refresh();
      } finally {
        setConfirmOpen(false);
      }
    },
    [
      hasChanges,
      isSaving,
      request,
      save,
      updateDefaults,
      tenantName,
      dispatch,
      notify,
      t,
      refresh,
      setConfirmOpen,
    ]
  );
  useEffect(() => {
    if (!isActive) return;
    setTabActions({
      hasChanges,
      isSaving,
      isChecking,
      conflicts,
      onSave,
      onDiscard: discard,
      saveAvailability: request === null ? "blocked" : "ready",
    });
    return () => {
      setTabActions(null);
    };
  }, [
    isActive,
    setTabActions,
    hasChanges,
    isSaving,
    isChecking,
    conflicts,
    onSave,
    discard,
    request,
  ]);
  const gradesToReview = [
    ...new Set(
      conflicts
        .filter((conflict) =>
          conflict.material_physics.some(
            (reference) => reference.property === "yield_percent"
          )
        )
        .flatMap((conflict) => conflict.steel_grade_names)
    ),
  ];
  const restorable = useMemo(
    () =>
      (scrapchef.currentData?.items ?? []).filter(
        (physics) =>
          materials.some(
            (material) =>
              material.id === physics.material_id &&
              material.visibility === "visible"
          ) &&
          source.items.some(
            (material) => material.material_id === physics.material_id
          )
      ),
    [scrapchef.currentData, materials, source]
  );
  const effectivePhysics = useMemo(
    () =>
      new Map(
        [...source.items, ...overrides].map((physics) => [
          physics.material_id,
          physics,
        ])
      ),
    [source.items, overrides]
  );
  const canRevert = restorable.some(
    (physics) =>
      !editablePhysicsEqual(effectivePhysics.get(physics.material_id), physics)
  );
  const matchesScrapchefDefaults =
    scrapchef.currentData === undefined
      ? null
      : physicsMatchesDefaults({
          physics: new Map(
            defaultSet.items.map((physics) => [physics.material_id, physics])
          ),
          defaults: scrapchef.currentData.items,
        });
  return (
    <MaterialDefaultsEditorLayout
      description={t("defaultPhysicsDescription")}
      matchesScrapchefDefaults={matchesScrapchefDefaults}
      revertButton={
        <RevertDefaultsButton
          disabled={!canRevert || isSaving || isStale}
          warning={t("revertPhysicsWarning")}
          onRevert={() => {
            setOverrides((overrides) => [
              ...overrides.filter(
                (material) =>
                  !restorable.some(
                    (restored) => restored.material_id === material.material_id
                  )
              ),
              ...restorable.map(editablePhysics),
            ]);
          }}
        />
      }
      unavailableWarning={
        scrapchef.isError && !scrapchef.currentData
          ? t("scrapchefPhysicsUnavailable")
          : null
      }
      staleWarning={isStale ? t("physicsDefaultsChanged") : null}
      onDiscard={discard}
      conflictWarning={{
        hasError,
        conflicts,
        isChecking,
        chefGroupNameById,
        limitLabelBySignature,
      }}
    >
      <ReviewFixedMixesButton
        gradeNames={gradesToReview}
        hasChanges={hasChanges}
        isSaving={isSaving}
        isChecking={isChecking}
        disabled={isStale || hasError || request === null}
        conflicts={conflicts}
        onSave={onSave}
      />
      <MaterialPhysicsDefaultsTable
        materials={materials}
        source={source.items}
        overrides={overrides}
        scrapchef={scrapchef.currentData?.items ?? []}
        conflicts={isChecking || hasError || request === null ? [] : conflicts}
        chefGroupNameById={chefGroupNameById}
        limitLabelBySignature={limitLabelBySignature}
        disabled={isSaving || isStale}
        onUpdate={(physics) => {
          setOverrides((overrides) =>
            replacePhysicsOverride({ overrides, physics })
          );
        }}
        onDiscard={(materialId) => {
          setOverrides((overrides) =>
            overrides.filter((material) => material.material_id !== materialId)
          );
        }}
      />
    </MaterialDefaultsEditorLayout>
  );
};
